if (!customElements.get('remiscent-finder')) {
  class RemiscentFinder extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.panels = Array.from(this.querySelectorAll('[data-finder-panel]'));
      this.progress = this.querySelector('[data-finder-progress]');
      this.progressBar = this.progress.parentElement;
      this.stepLabel = this.querySelector('[data-finder-step-label]');
      this.currentStep = 1;
      this.totalSteps = 6;
      this.currency = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
      this.profiles = {
        fresh: {
          handle: 'summer-fragrances',
          title: 'Fresh and uplifting',
          description: 'Bright citrus, airy woods and easy-to-wear scents with a clean sense of movement.'
        },
        floral: {
          handle: 'designer-fragrances',
          title: 'Polished florals',
          description: 'Floral perfume samples with softness, clarity and enough character to stay interesting.'
        },
        gourmand: {
          handle: 'best-sellers',
          title: 'Warm and addictive',
          description: 'Sweet, ambered and gourmand fragrances chosen for comfort, richness and lasting presence.'
        },
        woody: {
          handle: 'niche-artisan-indie-fragrances',
          title: 'Woods with character',
          description: 'Textural woods, resins and earthy notes from distinctive niche and independent houses.'
        },
        oud: {
          handle: 'oud-fragrances',
          title: 'Oud and deep resins',
          description: 'Dark, opulent perfume samples shaped by oud, smoke, spice and polished woods.'
        },
        bold: {
          handle: 'beast-mode-fragrances',
          title: 'Bold and long-lasting',
          description: 'Confident fragrances with noticeable projection and a strong, memorable trail.'
        },
        daring: {
          handle: 'daring-fragrances',
          title: 'Unusual and daring',
          description: 'Creative perfumes for curious noses: surprising contrasts, uncommon notes and real personality.'
        },
        classic: {
          handle: 'classic-rare-fragrances',
          title: 'Elegant classics and rarities',
          description: 'Refined compositions, nostalgic signatures and harder-to-find fragrance samples.'
        },
        niche: {
          handle: 'niche-artisan-indie-fragrances',
          title: 'Niche, artisan and indie',
          description: 'Independent ideas, beautiful materials and fragrances that step beyond the familiar.'
        },
        designer: {
          handle: 'designer-fragrances',
          title: 'Designer favourites',
          description: 'Recognisable houses and polished signatures, from modern releases to much-loved classics.'
        },
        value: {
          handle: 'good-value-inspired-fragrances',
          title: 'Good-value discoveries',
          description: 'Enjoyable, accessible perfume samples chosen for character and value.'
        }
      };
    }

    connectedCallback() {
      this.addEventListener('click', (event) => {
        const next = event.target.closest('[data-finder-next]');
        const back = event.target.closest('[data-finder-back]');
        const review = event.target.closest('[data-finder-review]');
        if (next) this.next();
        if (back) this.goTo(this.currentStep - 1);
        if (review) this.reviewAnswers();
      });

      this.form.addEventListener('submit', (event) => event.preventDefault());
      this.goTo(1, false);
    }

    validateStep() {
      const panel = this.panels.find((item) => Number(item.dataset.finderPanel) === this.currentStep);
      const error = panel.querySelector('[data-finder-error]');
      const requiredGroups = Array.from(panel.querySelectorAll('[data-required-group]'));
      const incomplete = requiredGroups.find((group) => !group.querySelector('input:checked'));

      if (incomplete) {
        error.textContent = incomplete.dataset.error || 'Please choose an option before continuing.';
        incomplete.querySelector('input')?.focus();
        return false;
      }

      error.textContent = '';
      return true;
    }

    next() {
      if (!this.validateStep()) return;
      if (this.currentStep < this.totalSteps) {
        this.goTo(this.currentStep + 1);
      } else {
        this.showResults();
      }
    }

    goTo(step, focus = true) {
      this.currentStep = Math.min(Math.max(step, 1), this.totalSteps);
      this.panels.forEach((panel) => {
        panel.hidden = Number(panel.dataset.finderPanel) !== this.currentStep;
      });
      this.progress.style.width = `${(this.currentStep / this.totalSteps) * 100}%`;
      this.progressBar.setAttribute('aria-valuenow', String(this.currentStep));
      this.stepLabel.textContent = `Step ${this.currentStep} of ${this.totalSteps}`;
      if (focus) {
        this.panels.find((panel) => !panel.hidden)?.querySelector('h2')?.focus({ preventScroll: true });
        this.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      }
    }

    answers() {
      const data = new FormData(this.form);
      return {
        recipient: data.get('recipient'),
        families: data.getAll('families'),
        occasion: data.get('occasion'),
        season: data.get('season'),
        mood: data.get('mood'),
        priority: data.get('priority')
      };
    }

    rankProfiles(answers) {
      const scores = Object.fromEntries(Object.keys(this.profiles).map((key) => [key, 0]));
      const add = (key, points = 1) => { if (key in scores) scores[key] += points; };

      answers.families.forEach((family) => add(family, 5));
      if (answers.recipient === 'gift') { add('classic', 2); add('designer', 1); }

      const occasionMap = {
        everyday: ['fresh', 'designer', 'value'],
        work: ['fresh', 'classic', 'designer'],
        evening: ['bold', 'oud', 'daring'],
        date: ['gourmand', 'floral', 'daring'],
        any: ['niche', 'designer', 'fresh']
      };
      (occasionMap[answers.occasion] || []).forEach((key, index) => add(key, 3 - index * 0.5));

      if (answers.season === 'warm') { add('fresh', 4); add('floral', 2); }
      if (answers.season === 'cold') { add('gourmand', 4); add('oud', 3); add('woody', 2); }
      if (answers.season === 'all') { add('designer', 2); add('niche', 2); }

      const moodMap = {
        clean: ['fresh', 'designer'],
        elegant: ['classic', 'floral', 'designer'],
        cozy: ['gourmand', 'woody'],
        bold: ['bold', 'oud'],
        mysterious: ['daring', 'oud', 'woody'],
        curious: ['niche', 'daring']
      };
      (moodMap[answers.mood] || []).forEach((key, index) => add(key, 4 - index));

      if (answers.priority === 'niche') { add('niche', 7); add('daring', 2); }
      if (answers.priority === 'designer') add('designer', 7);
      if (answers.priority === 'value') add('value', 8);
      if (answers.priority === 'rare') { add('classic', 7); add('daring', 2); }
      if (answers.priority === 'open') { add('niche', 2); add('designer', 2); add('classic', 1); }

      const ranked = Object.entries(scores)
        .sort((a, b) => b[1] - a[1])
        .map(([key]) => ({ key, ...this.profiles[key] }));

      const uniqueHandles = new Set();
      return ranked.filter((profile) => {
        if (uniqueHandles.has(profile.handle)) return false;
        uniqueHandles.add(profile.handle);
        return true;
      }).slice(0, 3);
    }

    resultSummary(answers, ranked) {
      const familyLabels = {
        fresh: 'fresh and citrus-led',
        floral: 'floral',
        gourmand: 'sweet and gourmand',
        woody: 'woody',
        oud: 'oud-led and smoky',
        daring: 'unusual'
      };
      const occasionLabels = {
        everyday: 'everyday wear',
        work: 'work and polished everyday moments',
        evening: 'evenings',
        date: 'dates and special occasions',
        any: 'different occasions'
      };
      const seasonLabels = {
        warm: 'warmer weather',
        cold: 'cooler weather',
        all: 'any season'
      };
      const moodLabels = {
        clean: 'clean, effortless',
        elegant: 'elegant',
        cozy: 'cosy, warm',
        bold: 'bold',
        mysterious: 'mysterious',
        curious: 'curious, unconventional'
      };

      const families = answers.families.map((family) => familyLabels[family]).filter(Boolean);
      const mood = moodLabels[answers.mood] || 'characterful';
      const occasion = occasionLabels[answers.occasion] || 'different occasions';
      const season = seasonLabels[answers.season] || 'any season';
      const familyDirection = families.length > 1
        ? `${families[0]} fragrances, while keeping a few contrasting styles open`
        : `${families[0] || 'characterful'} fragrances`;
      const introduction = `You’re drawn to ${familyDirection}. The mood feels ${mood}—something for ${occasion} that can work in ${season}.`;
      const giftNote = answers.recipient === 'gift'
        ? ' Because this is a gift, we’ve kept wearability in mind without making the suggestions feel too safe.'
        : '';
      const direction = ranked[1]
        ? ` We’d begin with ${ranked[0].title}; ${ranked[1].title} offers a useful contrast.`
        : ` We’d begin with ${ranked[0].title}.`;

      return `${introduction}${giftNote}${direction}`;
    }

    async showResults() {
      const answers = this.answers();
      const ranked = this.rankProfiles(answers);
      this.panels.forEach((panel) => { panel.hidden = true; });

      const results = this.querySelector('[data-finder-results]');
      results.hidden = false;
      this.progress.style.width = '100%';
      this.progressBar.setAttribute('aria-valuenow', String(this.totalSteps));
      this.stepLabel.textContent = 'Your matches';

      const summary = results.querySelector('[data-results-summary]');
      summary.textContent = this.resultSummary(answers, ranked);

      const collectionContainer = results.querySelector('[data-collection-results]');
      const resultLabels = ['Our first suggestion', 'Also worth trying', 'For a little contrast'];
      collectionContainer.innerHTML = ranked.map((profile, index) => `
        <article class="rs-finder-result">
          <span class="rs-finder-result__rank">${resultLabels[index]}</span>
          <h3>${this.escape(profile.title)}</h3>
          <p>${this.escape(profile.description)}</p>
          <a href="/collections/${encodeURIComponent(profile.handle)}">Explore this collection →</a>
        </article>
      `).join('');

      results.querySelector('h2')?.focus({ preventScroll: true });
      this.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });

      window.dataLayer?.push({ event: 'perfume_finder_complete', finder_matches: ranked.map((item) => item.handle).join(',') });
      this.dispatchEvent(new CustomEvent('remiscent:finder-complete', { bubbles: true, detail: { matches: ranked } }));
      await this.loadProducts(ranked, results.querySelector('[data-product-results]'));
    }

    async loadProducts(ranked, container) {
      container.innerHTML = '<p class="rs-finder__loading">Finding samples from your matches…</p>';
      container.setAttribute('aria-busy', 'true');
      try {
        const responses = await Promise.allSettled(
          ranked.map((profile) => fetch(`/collections/${encodeURIComponent(profile.handle)}/products.json?limit=4`).then((response) => {
            if (!response.ok) throw new Error('Collection unavailable');
            return response.json();
          }))
        );
        const seen = new Set();
        const products = responses.flatMap((response) => (
          response.status === 'fulfilled' ? response.value.products || [] : []
        )).filter((product) => {
          if (seen.has(product.id)) return false;
          seen.add(product.id);
          return product.images?.length && product.variants?.some((variant) => variant.available);
        }).slice(0, 6);

        if (!products.length) {
          container.innerHTML = '<p class="rs-finder__loading">Open the matched collections above to see the latest available samples.</p>';
          return;
        }

        container.innerHTML = products.map((product) => {
          const availablePrices = product.variants
            .filter((variant) => variant.available)
            .map((variant) => Number(variant.price))
            .filter(Number.isFinite);
          const price = availablePrices.length ? Math.min(...availablePrices) : 0;
          const image = product.images[0]?.src || product.images[0];
          return `
            <a class="rs-finder-product" href="/products/${encodeURIComponent(product.handle)}">
              <img src="${this.escape(image)}" alt="${this.escape(product.title)} perfume sample" width="480" height="480" loading="lazy">
              <strong>${this.escape(product.title)}</strong>
              <span>From ${this.currency.format(price)}</span>
            </a>
          `;
        }).join('');
      } catch (error) {
        container.innerHTML = '<p class="rs-finder__loading">Open the matched collections above to see the latest available samples.</p>';
      } finally {
        container.removeAttribute('aria-busy');
      }
    }

    reviewAnswers() {
      this.querySelector('[data-finder-results]').hidden = true;
      this.querySelector('[data-collection-results]').innerHTML = '';
      this.querySelector('[data-product-results]').innerHTML = '';
      this.goTo(1);
    }

    escape(value) {
      const element = document.createElement('div');
      element.textContent = String(value || '');
      return element.innerHTML;
    }
  }

  customElements.define('remiscent-finder', RemiscentFinder);
}
